"use client";

import { useState, useEffect, useMemo } from "react";
import FreeVhfLessonCtas from "@/components/FreeVhfLessonCtas";
import { dispatchFreeVhfLessonStarted } from "@/components/FreeVhfLeaveConfirm";
import FreeVhfSelfReport, { type PersistKey } from "@/components/FreeVhfSelfReport";
import { useUI } from "@/lib/ui-context";
import { useFreeVhfPersistence } from "@/hooks/use-free-vhf-persistence";
import { PersistQuestion, M2Question } from "@/lib/free-vhf-engagement-storage";

export interface QuestionOption {
  value: string;
  label: string;
}

export interface QuestionDef {
  id: "understand" | "comfortable" | "who" | "clear" | "confidence";
  question: string;
  options: QuestionOption[];
  multiple?: boolean;
}

export interface FreeVhfLessonGateProps {
  scenarioId: string;
  module: 1 | 2;
  questions: QuestionDef[];
  nextLessonHref?: string;
  endModuleHref?: string;
  nextLessonId?: string;
  hasNext: boolean;
  children?: React.ReactNode;
}

/**
 * Unified lesson gate: renders multiple self-reports and CTAs.
 * Next/End is disabled until ALL questions are answered.
 */
export default function FreeVhfLessonGate({
  scenarioId,
  module,
  questions,
  nextLessonHref,
  endModuleHref,
  nextLessonId,
  hasNext,
  children,
}: FreeVhfLessonGateProps) {
  const ui = useUI();
  const progression = ui.freeVhf?.progression;
  const nextDisabledHint = progression?.nextDisabledHint;
  const persistence = useFreeVhfPersistence(scenarioId);

  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    if (!persistence.isLoaded) return;
    const initialAnswers: Record<string, boolean> = {};
    questions.forEach(q => {
      let val: string | undefined;
      if (module === 1) {
        val = persistence.getM1Answer(q.id as PersistQuestion);
      } else if (module === 2) {
        val = persistence.getM2Answer(q.id as M2Question);
      }
      if (val) initialAnswers[q.id] = true;
    });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAnswers(prev => {
      const hasNew = Object.keys(initialAnswers).some(k => !prev[k]);
      if (!hasNew) return prev;
      return { ...prev, ...initialAnswers };
    });
  }, [persistence.isLoaded, scenarioId, module]); // eslint-disable-line react-hooks/exhaustive-deps

  const allAnswered = questions.every(q => answers[q.id]);

  const persistKeys = useMemo(() => {
    return questions.map(q => {
      if (module === 1) {
        if (q.id === "understand" || q.id === "comfortable") {
          return { scenarioId, question: q.id } as PersistKey;
        }
      } else if (module === 2) {
        if (q.id === "who" || q.id === "clear") {
          return { scenarioId, question: q.id, module: "m2" } as PersistKey;
        }
      }
      return undefined;
    });
  }, [module, questions, scenarioId]);

  return (
    <>
      {questions.map((q, idx) => (
        <FreeVhfSelfReport
          key={q.id}
          question={q.question}
          options={q.options}
          idPrefix={`${q.id}-${scenarioId}`}
          multiple={q.multiple}
          persistKey={persistKeys[idx]}
          onAnswer={() => {
            setAnswers(prev => {
              if (prev[q.id]) return prev;
              return { ...prev, [q.id]: true };
            });
            dispatchFreeVhfLessonStarted();
          }}
        />
      ))}
      {children}
      <FreeVhfLessonCtas
        nextLessonHref={nextLessonHref}
        endModuleHref={endModuleHref}
        module={module}
        nextLessonId={nextLessonId}
        hasNext={hasNext}
        nextDisabled={!allAnswered}
        nextDisabledHint={nextDisabledHint}
      />
    </>
  );
}
